import { z } from 'zod';
import { costSplitInput } from './cost-center-split.ts';
import { PAYMENT_METHODS } from './partner-bank.ts';
import { decimalString } from './schemas.ts';
import { fromScaled, toScaled } from './money.ts';
import { addWorkingDays, workingDaysBetween, type CalendarSnapshot } from './sla.ts';

/**
 * Contas a Pagar, entrega C1 (ADR 0081): entradas da API e o aging. As chaves de origem, as
 * parcelas e os movimentos seguem `finance-integration.ts`; a aptidão e as condições de
 * pagamento, `payment-readiness.ts` e `payment-terms.ts`.
 */

export const PAYABLE_STATUSES = [
  'draft',
  'in_approval',
  'approved',
  'partially_paid',
  'paid',
  'cancelled',
  'rejected',
] as const;
export type PayableStatus = (typeof PAYABLE_STATUSES)[number];

export const WITHHOLDING_CODES = [
  'IRRF',
  'ISS',
  'INSS',
  'PIS',
  'COFINS',
  'CSLL',
  'PCC',
  'OTHER',
] as const;

const amount = decimalString(15, 4);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

/**
 * Valor da retenção pela base e pela alíquota digitadas, arredondado ao centavo (meio para cima).
 * Só multiplica o que foi informado: quais impostos cabem continua com quem lança (D-12). Nulo se
 * faltar base ou alíquota, ou se não forem números positivos.
 */
export function withholdingFromRate(base: string, rate: string): string | null {
  let b: bigint;
  let r: bigint;
  try {
    b = toScaled(base);
    r = toScaled(rate);
  } catch {
    return null;
  }
  if (b <= 0n || r <= 0n) return null;
  // base e alíquota em 4 casas: base × alíquota ÷ 100 dá o valor; em centavos, ÷ 10^8.
  const cents = (b * r + 50_000_000n) / 100_000_000n;
  return `${cents / 100n}.${String(cents % 100n).padStart(2, '0')}`;
}

const withholdingInput = z.object({
  taxCode: z.enum(WITHHOLDING_CODES),
  baseAmount: amount.nullable().optional(),
  rate: decimalString(3, 4).nullable().optional(),
  amount,
  note: optionalText(300),
});

const termsInput = z.object({
  source: z.enum(['origin_document', 'partner_default', 'manual']),
  netDays: z.number().int().min(0).max(365),
  installments: z.number().int().min(1).max(60),
  dueDay: z.number().int().min(1).max(31).nullable().optional(),
  /** Texto livre do documento de origem, guardado como veio (não calcula nada). */
  originText: optionalText(500),
  /** Data a partir da qual os vencimentos contam (emissão do documento ou aceite). */
  baseDate: isoDate,
  /**
   * Motivo de quem lança com condição ou vencimentos diferentes dos do pedido ou do contrato
   * (ADR 0104, D7). Obrigatório só nesse caso; fica no título e no histórico.
   */
  overrideReason: z
    .string()
    .trim()
    .max(1000)
    .transform((v) => (v === '' ? null : v))
    .refine((v) => v == null || v.length >= 3, 'Escreva o motivo com pelo menos 3 letras')
    .nullable()
    .optional(),
});

const allocationLine = z.object({
  costCenterId: z.uuid(),
  percent: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, 'Percentual inválido'),
});

const common = {
  legalEntityId: z.uuid(),
  branchId: z.uuid().nullable().optional(),
  documentType: optionalText(40),
  documentNumber: optionalText(60),
  documentSeries: optionalText(10),
  documentDate: isoDate.nullable().optional(),
  description: z.string().trim().min(3).max(500),
  /** Mês de competência (AAAA-MM). */
  competence: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Competência no formato AAAA-MM'),
  grossAmount: amount,
  withholdings: z.array(withholdingInput).max(10).default([]),
  expenseTypeId: z.uuid(),
  classificationId: z.uuid(),
  costCenterId: z.uuid(),
  allocation: z.array(allocationLine).max(30).default([]),
  paymentMethod: z.enum(PAYMENT_METHODS),
  partnerBankAccountKey: z.uuid().nullable().optional(),
  terms: termsInput,
  /** Nota de fornecedor de onde o título nasce (ADR 0081, C9): o documento vem dela. */
  invoiceId: z.uuid().nullable().optional(),
  /**
   * "Essa compra afetará o CAC?" (C12): obrigatória no título avulso de centro de marketing; no
   * título de pedido, vale a resposta da compra (o banco ignora a informada).
   */
  affectsCac: z.boolean().nullable().optional(),
};

export const payableInput = z.discriminatedUnion('originKind', [
  z.object({
    originKind: z.literal('manual'),
    ...common,
    partnerId: z.uuid(),
    /** Moeda do documento; sem ela, a moeda funcional da empresa. */
    currency: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{3}$/, 'Moeda inválida')
      .nullable()
      .optional(),
    documentNumber: z.string().trim().min(1).max(60),
    documentType: z.string().trim().min(1).max(40),
    /**
     * Rateio do título avulso pela tela (ADR 0092): percentual ou valor fixo entre centros da
     * empresa do título, conferido e calculado pelo motor comum. O centro principal
     * (`costCenterId`) é uma das linhas: é a área pagadora e o centro da aprovação. Não convive
     * com `allocation` (percentuais prontos, o formato antigo, aceito como antes).
     */
    split: costSplitInput.nullish(),
  }),
  z.object({
    originKind: z.literal('purchase_order'),
    ...common,
    orderItemId: z.uuid(),
    billing: z
      .discriminatedUnion('basis', [
        z.object({ basis: z.literal('acceptance'), acceptanceLineId: z.uuid() }),
        z.object({ basis: z.literal('milestone'), milestoneNo: z.number().int().min(1).max(200) }),
        z.object({ basis: z.literal('period'), periodStart: isoDate }),
      ])
      .nullable()
      .optional(),
  }),
]);
export type PayableInput = z.infer<typeof payableInput>;

export const scheduleInput = z.object({
  scheduledFor: isoDate,
  treasuryAccountKey: z.uuid(),
});

/**
 * Valor que sai do caixa no pagamento (ADR 0081, C11; ADR 0084, D-19): o principal (que abate a
 * parcela) mais juros e multa, menos o desconto. Ex.: 100 + 2 - 0 = 102; 100 + 0 - 5 = 95.
 */
export function paymentCashAmount(
  principal: string,
  charges: { interest?: string | null; fine?: string | null; discount?: string | null } = {},
): string {
  return fromScaled(
    toScaled(principal) +
      toScaled(charges.interest ?? '0') +
      toScaled(charges.fine ?? '0') -
      toScaled(charges.discount ?? '0'),
  );
}

/**
 * Ocorrência de marketplace (C15): a plataforma já cobrou antes do aceite. É só um sinal, com a data
 * e o comprovante reais; não libera o pagamento.
 */
export const platformOccurrenceInput = z.object({
  paidOn: isoDate,
  amount,
  reference: z.string().trim().min(3).max(200),
  note: optionalText(1000),
});

export const paymentInput = z
  .object({
    /** O que abate do saldo da parcela (principal). */
    amount,
    /** Juros, multa e desconto do pagamento, na moeda do título (D-19). */
    interestAmount: amount.default('0'),
    fineAmount: amount.default('0'),
    discountAmount: amount.default('0'),
    occurredOn: isoDate,
    treasuryAccountKey: z.uuid(),
    partnerBankAccountId: z.uuid().nullable().optional(),
    reference: optionalText(200),
    /** Nº de Registro do NetSuite do pagamento (ADR 0073): só referência, sem integração. */
    netsuiteRecordNumber: optionalText(100),
    /** Alertas da aptidão (R4) que quem registra conferiu. */
    acknowledgeWarnings: z.array(z.string().max(60)).max(10).default([]),
  })
  .refine((v) => toScaled(v.discountAmount) <= toScaled(v.amount), {
    message: 'O desconto não passa do valor abatido da parcela',
    path: ['discountAmount'],
  })
  .refine(
    (v) =>
      toScaled(
        paymentCashAmount(v.amount, {
          interest: v.interestAmount,
          fine: v.fineAmount,
          discount: v.discountAmount,
        }),
      ) > 0n,
    { message: 'O valor pago precisa ser maior que zero', path: ['discountAmount'] },
  );

export const reverseInput = z.object({ note: z.string().trim().min(3).max(1000) });
export const cancelInput = z.object({ reason: z.string().trim().min(3).max(1000) });

export const payableCode = (n: number) => `CP-${String(n).padStart(6, '0')}`;

/** Espera pelo gestor no título provisório, em dias úteis, em cada etapa (decisão de 30/10/2026). */
export const PROVISIONAL_WAIT_BUSINESS_DAYS = 2;

/**
 * Datas-limite do título provisório, pelo calendário comum. Se o vencimento chega antes das duas
 * esperas, os dias úteis disponíveis são divididos entre elas, para não gerar juros nem multa.
 */
export function provisionalDeadlines(cal: CalendarSnapshot, startDate: string, dueDate: string) {
  const available = Math.min(
    workingDaysBetween(cal, startDate, dueDate),
    PROVISIONAL_WAIT_BUSINESS_DAYS * 2,
  );
  return {
    firstDeadline: addWorkingDays(cal, startDate, Math.ceil(available / 2)),
    finalDeadline: addWorkingDays(cal, startDate, available),
  };
}

/** Resposta do gestor ao título provisório no Master (ADR 0094): erro de cadastro exige o motivo. */
export const provisionalReviewAnswerInput = z
  .object({
    answer: z.enum(['confirmed', 'cadastre_error']),
    note: z.string().trim().min(3).max(1000).optional(),
  })
  .refine((v) => v.answer === 'confirmed' || v.note, {
    message: 'Explique o erro de cadastro',
    path: ['note'],
  });

// ----- Aging -----

export const AGING_BUCKETS = ['not_due', 'd1_30', 'd31_60', 'd61_90', 'over_90'] as const;
export type AgingBucket = (typeof AGING_BUCKETS)[number];

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

export function agingBucket(dueDate: string, asOf: string): AgingBucket {
  const late = daysBetween(dueDate, asOf);
  if (late <= 0) return 'not_due';
  if (late <= 30) return 'd1_30';
  if (late <= 60) return 'd31_60';
  if (late <= 90) return 'd61_90';
  return 'over_90';
}

/**
 * Recortes da lista por parcela que os cartões da Visão geral abrem (ADR 0097). A lista e o cartão
 * passam pelas mesmas regras: o número é sempre a lista.
 */
export const INSTALLMENT_INDICATORS = ['overdue', 'scheduled_past'] as const;
export type InstallmentIndicator = (typeof INSTALLMENT_INDICATORS)[number];
export const installmentIndicator = z.enum(INSTALLMENT_INDICATORS);

/** Vencida na data de corte e com saldo: o que o aging põe nas faixas de atraso. */
export function isOverdueInstallment(
  item: { dueDate: string; open: string },
  asOf: string,
): boolean {
  return toScaled(item.open) > 0n && agingBucket(item.dueDate, asOf) !== 'not_due';
}

/** Programada para um dia que já passou, sem pagamento registrado. */
export function isScheduledPast(
  item: { status: string; scheduledFor: string | null },
  today: string,
): boolean {
  return item.status === 'scheduled' && item.scheduledFor !== null && item.scheduledFor < today;
}

/**
 * Saldo em aberto por faixa de atraso na data de corte, por empresa e moeda (moedas nunca se
 * somam). Parcela paga ou cancelada não entra.
 */
export function agingSummary(
  items: readonly {
    legalEntityId: string;
    currency: string;
    dueDate: string;
    open: string;
  }[],
  asOf: string,
): {
  legalEntityId: string;
  currency: string;
  buckets: Record<AgingBucket, string>;
  total: string;
}[] {
  const groups = new Map<string, { legalEntityId: string; currency: string; v: bigint[] }>();
  for (const it of items) {
    const open = toScaled(it.open);
    if (open <= 0n) continue;
    const key = `${it.legalEntityId}|${it.currency}`;
    const g = groups.get(key) ?? {
      legalEntityId: it.legalEntityId,
      currency: it.currency,
      v: AGING_BUCKETS.map(() => 0n),
    };
    const i = AGING_BUCKETS.indexOf(agingBucket(it.dueDate, asOf));
    g.v[i] = g.v[i]! + open;
    groups.set(key, g);
  }
  return [...groups.values()].map((g) => ({
    legalEntityId: g.legalEntityId,
    currency: g.currency,
    buckets: Object.fromEntries(AGING_BUCKETS.map((b, i) => [b, fromScaled(g.v[i]!)])) as Record<
      AgingBucket,
      string
    >,
    total: fromScaled(g.v.reduce((s, x) => s + x, 0n)),
  }));
}
